import React from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../lib/utils';
import { Logo } from './Logo';
import {
  LayoutDashboard,
  Shield,
  ScrollText,
  Settings,
  ChevronLeft,
  Activity,
  Users,
  ShieldCheck
} from 'lucide-react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import logoImage from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

const navigation = [
  { name: 'Beranda', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'user'] },
  { name: 'Sertifikat', href: '/certificates', icon: Shield, roles: ['admin', 'user'] },
  { name: 'Status Server', href: '/agents', icon: Activity, roles: ['admin'] },
  { name: 'Log Aktivitas', href: '/logs', icon: ScrollText, roles: ['admin'] },
  { name: 'Manajemen User', href: '/users', icon: Users, roles: ['admin'] },
  { name: 'FortiWeb Config', href: '/fortiweb', icon: ShieldCheck, roles: ['admin', 'user'] },
  { name: 'Pengaturan', href: '/settings', icon: Settings, roles: ['admin'] },
];

export function Sidebar({ collapsed, onToggle }) {
  const { user } = useAuth();
  const { branding } = useBranding();

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(item =>
    item.roles.includes(user?.role || 'user')
  );

  return (
    <div
      className={cn(
        "flex flex-col h-full bg-card border-r transition-all duration-300 relative z-50",
        collapsed ? "w-16" : "w-64"
      )}
    >
      {/* Logo Header */}
      <div className={cn(
        "flex items-center h-20 border-b bg-card/50 backdrop-blur-sm transition-all duration-300",
        collapsed ? "justify-center px-0" : "px-5"
      )}>
        {!collapsed ? (
          <div className="flex items-center gap-3 w-full">
            <div className="flex-shrink-0 bg-white/10 p-1 rounded-lg backdrop-blur shadow-sm border border-border/50">
              <Logo
                src={branding.logo_url || logoImage}
                alt="Logo SSL Monitor"
                size="sm"
              />
            </div>
            <div className="flex flex-col min-w-0">
              <span className="font-bold text-[15px] leading-tight text-foreground truncate">
                Monitoring SSL
              </span>
              <span className="text-[13px] font-medium text-muted-foreground truncate">
                {branding.header_text.replace('Monitoring SSL ', '').replace('Monitoring SSL', '') || 'Dashboard'}
              </span>
            </div>
          </div>
        ) : (
          <div className="mx-auto flex-shrink-0 bg-white/10 p-1 rounded-lg backdrop-blur shadow-sm border border-border/50">
            <Logo
              src={branding.logo_url || logoImage}
              alt="Logo SSL Monitor"
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {filteredNavigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors my-1",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
                collapsed && "justify-center"
              )
            }
            title={collapsed ? item.name : undefined}
          >
            <item.icon className="h-5 w-5 flex-shrink-0" />
            {!collapsed && <span>{item.name}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Edge Hover Toggle Button */}
      <div className="absolute -right-3 top-5 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={onToggle}
          className="h-6 w-6 rounded-full bg-background/95 backdrop-blur shadow-sm border border-border hover:bg-accent hover:text-accent-foreground flex items-center justify-center p-0 transition-colors"
        >
          <ChevronLeft
            className={cn(
              "h-3.5 w-3.5 transition-transform duration-300",
              collapsed && "rotate-180"
            )}
          />
        </Button>
      </div>
    </div>
  );
}